import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { CalendarToday, Schedule, CheckCircle, Description } from '@mui/icons-material';
import ServiceSelection from './ServiceSelection';
import AppointmentCalendar from './AppointmentCalendar';
import AppointmentForm from './AppointmentForm';
import { appointmentService } from '../services/appointmentService';
import { useApp } from '../context/AppContext';

interface BookAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  caretakerId: string;
  caretakerName: string;
  caretakerAvatar?: string;
}

interface Service {
  id: string;
  name: string;
  description?: string | null;
  duration?: number | null;
  price?: number | string | null;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

const steps = [
  { label: 'Select Service', icon: <Schedule /> },
  { label: 'Choose Date & Time', icon: <CalendarToday /> },
  { label: 'Appointment Details', icon: <Description /> },
  { label: 'Review & Confirm', icon: <CheckCircle /> },
];

const BookAppointmentModal: React.FC<BookAppointmentModalProps> = ({
  open,
  onClose,
  caretakerId,
  caretakerName,
  caretakerAvatar,
}) => {
  const { state } = useApp();
  const user = state.user;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [appointmentId, setAppointmentId] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedService(null);
      setSelectedDate('');
      setSelectedTimeSlot(null);
      setAppointmentNotes('');
      setSpecialRequirements('');
      setAppointmentId(null);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleNext = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    handleNext();
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    setSelectedTimeSlot(timeSlot);
    handleNext();
  };

  const handleAppointmentSubmit = () => {
    if (!selectedService || !selectedDate || !selectedTimeSlot) {
      setError('Please complete all required fields');
      return;
    }
    handleNext();
  };

  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate all required data
      if (!selectedService || !selectedDate || !selectedTimeSlot) {
        setError('Missing required booking information. Please start over.');
        setLoading(false);
        return;
      }

      // Validate date/time is not in the past
      const appointmentDateTime = new Date(`${selectedDate}T${selectedTimeSlot.startTime}:00`);
      const now = new Date();
      if (appointmentDateTime < now) {
        setError('Cannot book appointments in the past. Please select a future date and time.');
        setLoading(false);
        return;
      }

      const appointmentData = {
        caretakerId,
        serviceId: selectedService.id,
        scheduledAt: appointmentDateTime.toISOString(),
        notes: appointmentNotes || undefined,
        specialRequirements: specialRequirements || undefined,
      };

      const appointment = await appointmentService.bookAppointment(appointmentData);
      setAppointmentId(appointment.id);
      setSuccess(true);
      handleNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit appointment request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (success) {
      // Refresh appointments or redirect to appointments page
      window.location.reload();
    }
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ServiceSelection
            caretakerId={caretakerId}
            onServiceSelect={handleServiceSelect}
            onError={setError}
          />
        );
      case 1:
        return (
          <AppointmentCalendar
            caretakerId={caretakerId}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onTimeSlotSelect={handleTimeSlotSelect}
            onError={setError}
          />
        );
      case 2:
        if (!selectedService || !selectedTimeSlot) {
          return (
            <Alert severity="error">
              Required information is missing. Please go back and complete the previous steps.
            </Alert>
          );
        }
        return (
          <AppointmentForm
            service={selectedService}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            notes={appointmentNotes}
            specialRequirements={specialRequirements}
            onNotesChange={setAppointmentNotes}
            onSpecialRequirementsChange={setSpecialRequirements}
            onSubmit={handleAppointmentSubmit}
          />
        );
      case 3:
        if (!selectedService || !selectedDate || !selectedTimeSlot) {
          return (
            <Alert severity="error">
              Required information is missing. Please go back and complete the previous steps.
            </Alert>
          );
        }
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Appointment Details
            </Typography>
            
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Service
                </Typography>
                <Typography variant="h6">{selectedService.name}</Typography>
                {selectedService.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedService.description}
                  </Typography>
                )}
                {selectedService.price != null && typeof selectedService.price === 'number' && (
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    ${selectedService.price.toFixed(2)}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date & Time
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
                <Typography variant="body1">
                  {selectedTimeSlot.startTime} - {selectedTimeSlot.endTime}
                </Typography>
              </CardContent>
            </Card>

            {(appointmentNotes || specialRequirements) && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  {appointmentNotes && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2">{appointmentNotes}</Typography>
                    </>
                  )}
                  {specialRequirements && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: appointmentNotes ? 2 : 0 }}>
                        Special Requirements
                      </Typography>
                      <Typography variant="body2">{specialRequirements}</Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This appointment is a request. The caregiver will review and accept your request. Once accepted, all payments will be handled directly by the caregiver. Careva does not process payments.
              </Typography>
            </Alert>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Submitting appointment request...
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 4:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Appointment Request Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your appointment request with {caretakerName} has been submitted. The caregiver will review and accept your request. Once accepted, payment will be handled directly by the caregiver.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Appointment ID: {appointmentId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {selectedDate} at {selectedTimeSlot?.startTime}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Service: {selectedService?.name}
              </Typography>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedService !== null;
      case 1:
        return selectedDate !== '' && selectedTimeSlot !== null;
      case 2:
        return true; // Form validation handled in component
      case 3:
        return !loading; // Can proceed if not currently loading
      case 4:
        return false; // Final step
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {caretakerAvatar && (
            <Box
              component="img"
              src={caretakerAvatar}
              alt={caretakerName}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          )}
          <Box>
            <Typography variant="h6">Book Appointment</Typography>
            <Typography variant="body2" color="text.secondary">
              with {caretakerName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: index <= activeStep ? 'primary.main' : 'grey.300',
                        color: index <= activeStep ? 'white' : 'grey.600',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose}>
          {success ? 'Done' : 'Cancel'}
        </Button>
        {activeStep > 0 && activeStep < 4 && !success && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < 3 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Next'}
          </Button>
        )}
        {activeStep === 3 && (
          <Button
            variant="contained"
            onClick={handleConfirmBooking}
            disabled={loading || !selectedService || !selectedDate || !selectedTimeSlot}
          >
            {loading ? <CircularProgress size={20} /> : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BookAppointmentModal;
